import { useMemo, useState } from "react";
import { uploadListingImagesToFirebase } from "../../components/Travel/Accomodations/uploadListingImagesToFirebase";
import { UploadFolder } from "../../types/upload.types";

type UseFirebaseImageUploadOptions = {
  folder: UploadFolder; // "cars" | "accommodations" | ...
  maxFiles?: number;
  initialUrls?: string[]; // ✅ for edit mode (existing Firebase links)
  initialImageUrl?: string; // ✅ backward compat (single imageUrl)
};

function cleanStrings(list?: any): string[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean);
}

export function useFirebaseImageUpload(options: UseFirebaseImageUploadOptions) {
  const { folder, maxFiles = 12, initialUrls, initialImageUrl } = options;

  // ✅ existing saved urls (from DB)
  const [urls, setUrls] = useState<string[]>(() => {
    const u = cleanStrings(initialUrls);
    if (u.length) return u;
    const single = (initialImageUrl || "").trim();
    return single ? [single] : [];
  });

  // ✅ newly picked files (not yet uploaded)
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ✅ combined previews: saved urls + local previews
  const previews = useMemo(() => {
    const local = files.map((f) => URL.createObjectURL(f));
    return [...urls, ...local];
  }, [urls, files]);

  const clearNewFiles = () => {
    setFiles([]);
    setErrorMsg(null);
  };

  // ✅ APPEND (does NOT replace)
  const onPickFiles = (picked: File[]) => {
    setErrorMsg(null);
    const safe = (picked || []).filter(Boolean);
    if (!safe.length) return;

    setFiles((prev) => {
      const merged = [...prev, ...safe];
      return merged.slice(0, maxFiles);
    });
  };

  // ✅ remove preview at index (works for both saved urls and local previews)
  const removeAt = (index: number) => {
    if (index < urls.length) {
      setUrls((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    const localIdx = index - urls.length;
    setFiles((prev) => prev.filter((_, i) => i !== localIdx));
  };

  /**
   * ✅ Uploads selected new files and appends to existing urls.
   * Returns final urls (existing + newly uploaded).
   */
  const uploadSelected = async (key: string): Promise<string[]> => {
    setErrorMsg(null);

    if (!files.length) return urls;

    setIsUploading(true);
    try {
      const uploaded = await uploadListingImagesToFirebase(files, key, folder);
      const merged = [...urls, ...uploaded];

      setUrls(merged);
      setFiles([]);
      return merged;
    } catch (e: any) {
      const msg = e?.message || "Failed to upload images";
      setErrorMsg(msg);
      throw e; // ✅ let form decide whether to block submit
    } finally {
      setIsUploading(false);
    }
  };

  return {
    // saved urls
    urls,
    setUrls,

    // new files
    files,
    isUploading,
    errorMsg,

    // previews + actions
    previews,
    onPickFiles,
    removeAt,
    clearNewFiles,

    // upload
    uploadSelected,
  };
}
